import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { ThumbsUp, ThumbsDown, EyeOff } from "lucide-react"
import { api } from "@/lib/api"
import { cn } from "@/lib/utils"

type Rating = "up" | "down" | "skip"
type Feedback = {
  paper_id: string
  rating: Rating
  created_at: string
  updated_at: string
} | null

const KEY = (paperId: string) => ["paper-feedback", paperId] as const

export function FeedbackBar({ paperId }: { paperId: string }) {
  const qc = useQueryClient()
  const { data } = useQuery({
    queryKey: KEY(paperId),
    queryFn: () => api.get<Feedback>(`/papers/${paperId}/feedback`),
  })
  const current = data?.rating ?? null

  const set = useMutation({
    mutationFn: (rating: Rating) =>
      api.put<Feedback>(`/papers/${paperId}/feedback`, { rating }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(paperId) }),
  })
  const clear = useMutation({
    mutationFn: () => api.delete<void>(`/papers/${paperId}/feedback`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(paperId) }),
  })

  const onClick = (next: Rating) => {
    if (current === next) clear.mutate()
    else set.mutate(next)
  }

  return (
    <section className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground">이 논문에 대한 평가:</span>
      <button
        type="button"
        onClick={() => onClick("up")}
        disabled={set.isPending || clear.isPending}
        className={cn(
          "inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs transition-colors",
          current === "up"
            ? "border-primary bg-primary/10 text-primary"
            : "border-border text-muted-foreground hover:bg-secondary",
        )}
      >
        <ThumbsUp className="h-3.5 w-3.5" /> 좋아요
      </button>
      <button
        type="button"
        onClick={() => onClick("down")}
        disabled={set.isPending || clear.isPending}
        className={cn(
          "inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs transition-colors",
          current === "down"
            ? "border-destructive bg-destructive/10 text-destructive"
            : "border-border text-muted-foreground hover:bg-secondary",
        )}
      >
        <ThumbsDown className="h-3.5 w-3.5" /> 별로
      </button>
      <button
        type="button"
        onClick={() => onClick("skip")}
        disabled={set.isPending || clear.isPending}
        className={cn(
          "inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs transition-colors",
          current === "skip"
            ? "border-foreground/40 bg-secondary text-foreground"
            : "border-border text-muted-foreground hover:bg-secondary",
        )}
      >
        <EyeOff className="h-3.5 w-3.5" /> 관심 없음
      </button>
    </section>
  )
}
